/* eslint-disable react/destructuring-assignment */
/* eslint-disable react/jsx-props-no-spreading */
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { fontSizes, inputLeftPadding, timeInputWidth, useGetWidthWithOverflow, styled } from "@iventis/styles";
import { TimeSelector } from "@iventis/components";
import { sanitiseForDOMToken } from "@iventis/utilities";

import { IventisTableData, RowNetworkStatus, IventisCellEditor, TimeOnlyCellEditorParams } from "../types/data-table.types";
import { TIME_RANGE_FROM, TIME_RANGE_TO } from "../lib/constants";

export const TimeOnlyCellEditor = forwardRef(_TimeOnlyCellEditor);

/**
 * Used as cellEditor for data table column of type "Time"
 */
// eslint-disable-next-line no-underscore-dangle
function _TimeOnlyCellEditor<TData extends IventisTableData = IventisTableData>(
    { node, required, validator, onDone, ...props }: TimeOnlyCellEditorParams<TData>,
    ref: React.ForwardedRef<IventisCellEditor>
) {
    const { ref: inputRef, width } = useGetWidthWithOverflow<HTMLDivElement>(null);
    const [value, setValue] = useState(props.value);
    const { doesFail, errorMessage } = validator?.(node, props.column.getColId() as keyof TData, value) ?? { doesFail: false };

    useEffect(() => {
        props.columnApi.setColumnWidth(props.column, width + 2);
    }, [width]);

    useImperativeHandle(ref, () => ({
        getValue() {
            onDone?.(value, node);
            return value;
        },
        isCancelBeforeStart() {
            // If the field is not required or has a value, while we are pending creation, make this cell uneditable
            const blockEditsUntilCreatedOnServer = (!required || props.value != null) && node.data.networkStatus === RowNetworkStatus.PendingCreation;
            return !node.data.canEdit || blockEditsUntilCreatedOnServer;
        },
        isCancelAfterEnd() {
            return value === props.value || doesFail;
        },
        isPopup() {
            return false;
        },
    }));

    /** Is the time field a range field */
    const isRange = [TIME_RANGE_FROM, TIME_RANGE_TO].some((range) => props.colDef.field.includes(range));

    return (
        <StyledTimeSelector
            width={`${Number(timeInputWidth.replace("px", "")) - 2}px`}
            additionalInputProps={{
                "data-testid": sanitiseForDOMToken(`data-table-${isRange ? "TimeRange" : "Time"}-cell-editor-row-${node.data?.name ?? ""}-column-${props.colDef.headerName}`),
            }}
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            variant="standard"
            helperText={doesFail && errorMessage}
        />
    );
}

const StyledTimeSelector = styled(TimeSelector)`
    .MuiInput-input {
        padding-left: ${inputLeftPadding};
        padding-right: ${inputLeftPadding};
        font-size: ${fontSizes.small};
    }
    .MuiInputBase-root {
        background-color: ${(props) => props.theme.dataTableTheme.background};
        // ToDo: Calculate this value using the gridOptions.rowHeight
        height: 40px;
        padding-top: 1px;
        font-size: ${fontSizes.small};
    }
    .fPYFwe:before,
    .fPYFwe:after,
    .fPYFwe:hover:before {
        border-bottom: 0;
    }
`;
